import type { FC } from "react";

import type { TGridFieldKeys } from "@ryde/shared";

// Imported from its own module, not the templates-edit barrel: that barrel imports this file back (through the
// templates-edit display components using CsvCellTemplate), and going through it makes the import cycle throw
// "Cannot access 'HiddenFieldTemplate' before initialization" at module evaluation.
import { HiddenFieldTemplate } from "../../templates-edit/hidden-field.template";
import { AddressCellTemplate } from "../address-cell.template";
import { AvailabilityCellTemplate } from "../availability-cell-template";
import { BookingHubCellTemplate } from "../booking-hub-cell-template";
import { BookingStatusCellTemplate } from "../booking-status-cell.template";
import { BooleanImageIconCellTemplate } from "../boolean-image-icon-cell.template";
import { ComplaintStatusCellTemplate } from "../complaint-status-cell.template";
import { CourseScheduleCellTemplate } from "../course-schedule-cell";
import { CourseStationsCellTemplate } from "../course-stations-cell";
import { CsvCellTemplate } from "../csv-list.template";
import { CurrencyCellTemplate } from "../currency-cell.template";
import { DataUploadRunButtonCellTemplate } from "../data-upload-run-button-cell.template";
import { DateCountdownCellTemplate } from "../date-countdown-cell.template";
import { DateEditableTemplate } from "../date-editable.template";
import { DateWithIndicatorCellTemplate } from "../date-with-indicator-cell.template";
import { DatetimeCompactReadonlyCellTemplate } from "../datetime-compact-readonly-cell.template";
import { DirectionCellTemplate } from "../direction-cell.template";
import { DiscountCellTemplate } from "../discount-cell.template";
import { DropdownBoundTextCellTemplate } from "../dropdown-bound-text-cell.template";
import { EmailCellTemplate } from "../email-cell.template";
import { EntityIdLinkTemplate } from "../entity-id-link.template";
import { EntityNameTextCellTemplate } from "../entity-text-cell.template";
import { FeedbackStatusCellTemplate } from "../feedback-status-cell.template";
import { FeedbacksCountCellTemplate } from "../feedbacks-count-cell.template";
import { GpsIndicatorCellTemplate } from "../gps-indicator-cell.template";
import { HubStationsCellTemplate } from "../hub-stations-cell-template";
import { JsonViewCellTemplate } from "../json-view-cell.template";
import { LiveMapArrivalDelayCellTemplate } from "../live-map-arrival-delay-cell.template";
import { LiveMapDelayStatusCellTemplate } from "../live-map-delay-status-cell.template";
import { LiveMapRideStartStatusCellTemplate } from "../live-map-ride-start-status-cell.template";
import { MlPathIndicatorCellTemplate } from "../ml-path-indicator-cell.template";
import { NotificationContentGridTemplate } from "../notification-content-grid.template";
import { NotificationPriorityCellTemplate } from "../notification-priority-cell-template";
import { NumberCellTemplate } from "../number-cell.template";
import { OpenEntityFormCellTemplate } from "../open-entity-form-cell-template";
import { PhoneCellTemplate } from "../phone-cell.template";
import { PlainTextCellTemplate } from "../plain-text-cell.template";
import { PriceRatePricingCellTemplate } from "../price-rate-pricing-cell.template";
import { ProductionActionButtonCellTemplate } from "../production-action-button-cell.template";
import { ProductionChainCellTemplate } from "../production-chain-cell.template";
import { RideEditableDocksDropdownTemplate } from "../ride-docks-editable-dropdown";
import { RideEditableDropdownBoundTemplate } from "../ride-editable-dropdown-bound";
import { RidePassengerCsvListCellTemplate } from "../ride-passengers-csv-list-cell.template";
import { RideRouteCompletionStatusCellTemplate } from "../ride-route-completion-status-cell.template";
import { RideStartEndDateEditableTemplate } from "../ride-start-end-date-editable.template";
import { RideStatusCellTemplate } from "../ride-status-cell.template";
import { SwitchAlternativeCellTemplate } from "../switch-alternative-cell.template";
import { SwitchCellTemplate } from "../switch-cell.template";
import { SwitchConfirmCellTemplate } from "../switch-confirm-cell.template";
import { TagsCellTemplate } from "../tags-cell.template";
import { TextCellTemplate } from "../text-cell.template";
import { TimeTextCellTemplate } from "../time-text-cell.template";
import { VehicleOccupancyCellTemplate } from "../vehicle-occupancy-cell.template";
import { WarningsDisplayCellTemplate } from "../warnings-display";
import type { IRenderColumnTemplate } from "./render.cell.template";

export const tableTemplateControlsMap: Partial<
  Record<TGridFieldKeys, FC<IRenderColumnTemplate>>
> = {
  address: AddressCellTemplate,
  boolean_image_icon: BooleanImageIconCellTemplate,
  booking_status: BookingStatusCellTemplate,
  feedback_status: FeedbackStatusCellTemplate,
  feedbacks_count: FeedbacksCountCellTemplate,
  gps_indicator: GpsIndicatorCellTemplate,
  ride_ml_path_indicator: MlPathIndicatorCellTemplate,
  ride_passengers_csv_list: RidePassengerCsvListCellTemplate,
  checkbox: TextCellTemplate,
  complaint_status: ComplaintStatusCellTemplate,
  course_schedule: CourseScheduleCellTemplate,
  course_stations: CourseStationsCellTemplate,
  csv_list: CsvCellTemplate,
  json_view: JsonViewCellTemplate,
  currency: CurrencyCellTemplate,
  currency_discount: DiscountCellTemplate,
  date: DateEditableTemplate,
  date_range: TextCellTemplate,
  date_with_indicator: DateWithIndicatorCellTemplate,
  datetime: DateEditableTemplate,
  datetime_compact_readonly: DatetimeCompactReadonlyCellTemplate,
  datetime_range: TextCellTemplate,
  direction: DirectionCellTemplate,
  dropdown_bound_text: DropdownBoundTextCellTemplate,
  entity_name: EntityNameTextCellTemplate,
  email: EmailCellTemplate,
  number: NumberCellTemplate,
  phone: PhoneCellTemplate,
  price_rate_pricing: PriceRatePricingCellTemplate,
  ride_status: RideStatusCellTemplate,
  switch: SwitchCellTemplate,
  switch_alternative: SwitchAlternativeCellTemplate,
  switch_confirm: SwitchConfirmCellTemplate,
  tags: TagsCellTemplate,
  text: TextCellTemplate,
  plain_text: PlainTextCellTemplate,
  time: TimeTextCellTemplate,
  time_range: TextCellTemplate,
  url: TextCellTemplate,
  vehicle_occupancy: VehicleOccupancyCellTemplate,
  ride_start_end_datetime: RideStartEndDateEditableTemplate,
  action_button: ProductionActionButtonCellTemplate,
  notification_priority: NotificationPriorityCellTemplate,
  date_countdown: DateCountdownCellTemplate,
  notification_grid_content: NotificationContentGridTemplate,
  entity_link: EntityIdLinkTemplate,
  open_entity_form: OpenEntityFormCellTemplate,
  ride_editable_dropdown_bound: RideEditableDropdownBoundTemplate,
  ride_editable_docks_dropdown: RideEditableDocksDropdownTemplate,
  warnings_display: WarningsDisplayCellTemplate,
  hidden_field: HiddenFieldTemplate, // Placeholder for hidden fields, no UI component needed
  hub_stations_grid_template: HubStationsCellTemplate,
  booking_hub_grid_template: BookingHubCellTemplate,
  table_availability: AvailabilityCellTemplate,
  live_map_arrival_delay: LiveMapArrivalDelayCellTemplate,
  live_map_delay_status: LiveMapDelayStatusCellTemplate,
  live_map_ride_start_status: LiveMapRideStartStatusCellTemplate,
  ride_route_completion_status: RideRouteCompletionStatusCellTemplate,
  production_chain: ProductionChainCellTemplate,
  data_upload_run_button: DataUploadRunButtonCellTemplate,
} as const;
